import { formatLiveSince } from '@/utils/postOp/message';
import { sampleAppointment } from '@/utils/formsSettings/editorModel';

/**
 * What the two SENDING tabs of Forms Settings (NewPatientsTab, MedHxTab) share: the host contract (src/utils/formsSettings/host.js), the slices of the
 * ONE context call as props, and the small bits of state every switch card / Save needs. A mixin, not a component: the host READS `dirty`, `errorCount`
 * and `saving` off the tab INSTANCE and calls validate() / save() / reset() on it.
 *
 *   the tab sets      tabKey: 'newPatients' | 'medHx'   (a component option — what it registers as)
 *   the tab defines   dirty, errorCount (computed) and validate(), save(), reset() (methods)
 *   this gives        register / unregister with the host, `saving` (own request in flight: Save OR the switch card's confirm / turn-off),
 *                     auth(), smsReady / emailReady, timezone, liveSinceTextFor(), the sample appointment
 *
 * ⛔ A tab's save() NEVER carries the switch (`enabled` / `activated`): only its SendSwitchCard's confirm does (plan D15a).
 */
export default {
  inject: {
    formsSettingsHost: { default: null },
  },
  props: {
    siteId: { type: String, default: null },
    /** response.newPatient / response.medHx of the context call. */
    context: { type: Object, default: null },
    /** response.forms [{ formIDForWeb, name, formType }] / response.packets [{ packetID, name, formIDs, isNewPatientPacket }]. */
    forms: { type: Array, default: () => [] },
    packets: { type: Array, default: () => [] },
    /** response.formsLoadFailed === true: the lists are empty because the read failed. */
    catalogFailed: { type: Boolean, default: false },
    /** { name, phone, senderEmail, signatureEnabled, signatureHtml } — the REAL practice, for the preview. */
    site: { type: Object, default: () => ({}) },
    /** response.readiness { smsReady, emailReady, … }. */
    readiness: { type: Object, default: null },
  },
  data() {
    return {
      saveBusy: false,
      switchBusy: false,
      switchError: '',
      /** What the server refused in the last request, one sentence each (a 400 INVALID_SETTINGS' problems). */
      serverProblems: [],
      // the sample appointment is fixed for the life of the tab (a week from when it opened)
      sample: sampleAppointment(),
    };
  },
  computed: {
    /** A request of THIS tab's own is in flight — Save, or its switch card's confirm / turn-off. */
    saving() { return this.saveBusy || this.switchBusy; },
    smsReady() { return !!(this.readiness && this.readiness.smsReady); },
    emailReady() { return !!(this.readiness && this.readiness.emailReady); },
    timezone() {
      const rows = (this.$store && this.$store.state.customer && this.$store.state.customer.settings) || [];
      const hit = Array.isArray(rows) ? rows.find((r) => r && r.Property === 'GENERAL_TIMEZONE') : null;
      return (hit && hit.PropertyValue) || '';
    },
  },
  created() {
    if (this.formsSettingsHost) this.formsSettingsHost.register(this.$options.tabKey, this);
  },
  beforeDestroy() {
    if (this.formsSettingsHost) this.formsSettingsHost.unregister(this.$options.tabKey, this);
  },
  methods: {
    auth() {
      return this.$store && this.$store.state.getAuth ? this.$store.state.getAuth() : null;
    },
    /** The server's liveSince (ISO) as "Oct 20, 2026, 2:10 PM" in the practice's timezone; '' when there is none. */
    liveSinceTextFor(iso) {
      return formatLiveSince(iso, this.timezone);
    },
    /** A refused request → the sentence for the switch card's error line, and the server's own problem list (if it sent one) under it. */
    showFailure(err, fallback) {
      const info = err && err.info;
      this.serverProblems = info && info.kind === 'invalid' ? info.problems.map((p) => p.message) : [];
      this.switchError = (info && info.message) || (err && err.message) || fallback;
    },
    clearFailure() {
      this.switchError = '';
      this.serverProblems = [];
    },
  },
};
